import { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import { getFullName, getInitials } from './staffMockData';
import {
    fetchStaffList, fetchDepartments, fetchRoles, fetchStates, fetchDistrictsByStateId, fetchCitiesByStateId,
    deleteStaff, createStaff, updateStaff, mapStaffToUi,
    type DepartmentOption, type RoleOption, type StateOption, type DistrictOption, type CityOption, type MappedStaff,
} from '../../services/staffApi';
import { fetchSchoolSubjects, type SchoolSubjectRecord } from '../../services/subjectApi';
import { getSchoolId } from '../../services/auth';
import './StaffManagement.css';
import { useTranslation, Trans } from 'react-i18next';

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

type DrawerMode = null | 'create' | 'edit';
type FormErrors = { firstName?: string; roleId?: string; department?: string; };

export default function StaffManagement() {
    const { t } = useTranslation();
    const [staff, setStaff] = useState<MappedStaff[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [search, setSearch] = useState('');
    const [filterDept, setFilterDept] = useState('');
    const [teachingOnly, setTeachingOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [departments, setDepartments] = useState<DepartmentOption[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [subjects, setSubjects] = useState<SchoolSubjectRecord[]>([]);
    const [states, setStates] = useState<StateOption[]>([]);
    const [districts, setDistricts] = useState<DistrictOption[]>([]);
    const [cities, setCities] = useState<CityOption[]>([]);
    const [addressLoading, setAddressLoading] = useState(false);

    /* ---- Drawer state ---- */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formFirstName, setFormFirstName] = useState('');
    const [formLastName, setFormLastName] = useState('');
    const [formEmail, setFormEmail] = useState('');
    const [formPhone, setFormPhone] = useState('');
    const [formRoleId, setFormRoleId] = useState<number | null>(null);
    const [formDob, setFormDob] = useState('');
    const [formGender, setFormGender] = useState('');
    const [formAddress, setFormAddress] = useState('');
    const [formState, setFormState] = useState('');
    const [_formStateId, setFormStateId] = useState<number | null>(null);
    const [formDistrict, setFormDistrict] = useState('');
    const [formCity, setFormCity] = useState('');
    const [formPostalCode, setFormPostalCode] = useState('');
    const [formCountry, setFormCountry] = useState('');
    const [formProfilePhoto, setFormProfilePhoto] = useState<File | null>(null);
    const [formPhotoPreview, setFormPhotoPreview] = useState<string | null>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const [formJoiningDate, setFormJoiningDate] = useState('');
    const [formIsTeacher, setFormIsTeacher] = useState(false);
    const [formSubjectId, setFormSubjectId] = useState('');
    const [formIsActive, setFormIsActive] = useState(true);
    const [formDepartmentId, setFormDepartmentId] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editCreatedAt, setEditCreatedAt] = useState('');
    const [editUpdatedAt, setEditUpdatedAt] = useState('');

    const [deleteTarget, setDeleteTarget] = useState<MappedStaff | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchStaffList({ page, perPage: 15, isTeacher: teachingOnly || undefined })
            .then((res) => {
                if (cancelled) return;
                setStaff(res.data.map(mapStaffToUi));
                setTotalPages(res.meta.last_page);
                setTotal(res.meta.total);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setError((err as { message?: string })?.message ?? t('staffMgmt.failedToLoad'));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, teachingOnly, refreshKey]);

    useEffect(() => {
        // Reset to page 1 when teaching filter changes
        setPage(1);
    }, [teachingOnly]);

    useEffect(() => {
        fetchDepartments().then(setDepartments).catch(() => {});
        fetchRoles().then(setRoles).catch(() => {});
        fetchStates().then(setStates).catch(() => {});
        const schoolId = getSchoolId();
        if (schoolId) fetchSchoolSubjects(schoolId, { perPage: 200, isActive: true }).then(setSubjects).catch(() => {});
    }, []);

    /* ---- Client-side filtering (search + department on the current page) ---- */
    const filtered = staff.filter((s) => {
        const q = search.toLowerCase();
        const matchSearch = !q || getFullName(s).toLowerCase().includes(q) || s.employee_id.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || s.designation.toLowerCase().includes(q);
        const matchDept = !filterDept || s.department === filterDept;
        return matchSearch && matchDept;
    });

    /* ---- Form helpers ---- */
    const resetForm = useCallback(() => {
        setFormFirstName(''); setFormLastName(''); setFormEmail(''); setFormPhone('');
        setFormRoleId(null); setFormDepartmentId('');
        setFormDob(''); setFormGender(''); setFormAddress('');
        setFormState(''); setFormStateId(null); setFormDistrict(''); setFormCity('');
        setFormPostalCode(''); setFormCountry('');
        setFormProfilePhoto(null); setFormPhotoPreview(null);
        setDistricts([]); setCities([]);
        if (photoInputRef.current) photoInputRef.current.value = '';
        setFormJoiningDate(''); setFormIsTeacher(false); setFormSubjectId(''); setFormIsActive(true);
        setErrors({}); setEditingId(null); setEditCreatedAt(''); setEditUpdatedAt('');
    }, []);

    const openCreate = () => { resetForm(); setDrawerMode('create'); };
    const openEdit = (s: MappedStaff) => {
        resetForm();
        setFormFirstName(s.first_name); setFormLastName(s.last_name || ''); setFormEmail(s.email);
        setFormPhone(s.phone || '');
        setFormDob(s.date_of_birth ?? ''); setFormGender(s.gender ?? '');
        setFormAddress(s.address ?? '');
        setFormState(s.state ?? ''); setFormDistrict(s.district ?? ''); setFormCity(s.city ?? '');
        setFormPostalCode(s.postal_code ?? '');
        const matchedState = states.find((st) => st.name === s.state);
        if (matchedState) {
            setFormStateId(matchedState.id);
            fetchDistrictsByStateId(matchedState.id).then(setDistricts).catch(() => {});
            fetchCitiesByStateId(matchedState.id).then(setCities).catch(() => {});
        }
        setFormCountry(s.country ?? ''); setFormProfilePhoto(null);
        setFormPhotoPreview(s.profile_photo_url ?? null);
        const matchedRole = roles.find((r) => r.name === s.designation);
        setFormRoleId(matchedRole?.id ?? null);
        setFormDepartmentId(s.department_id || '');
        setFormJoiningDate(s.joining_date); setFormIsTeacher(s.is_teacher);
        setFormSubjectId(s.primary_subject_id || ''); setFormIsActive(s.is_active);
        setEditingId(s.id); setEditCreatedAt(s.created_at); setEditUpdatedAt(s.updated_at);
        setDrawerMode('edit');
    };
    const closeDrawer = () => { setDrawerMode(null); resetForm(); };

    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!formFirstName.trim()) e.firstName = t('staffMgmt.firstNameRequired');
        if (!formRoleId) e.roleId = t('staffMgmt.designationRequired');
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);

        try {
            const schoolId = getSchoolId() || '';
            const fd = new FormData();
            fd.append('first_name', formFirstName.trim());
            if (formLastName.trim()) fd.append('last_name', formLastName.trim());
            fd.append('email', formEmail.trim());
            if (formPhone.trim()) fd.append('phone', formPhone.trim());
            if (formDob) fd.append('date_of_birth', formDob);
            if (formGender) fd.append('gender', formGender);
            if (formAddress.trim()) fd.append('address', formAddress.trim());
            if (formState) fd.append('state', formState);
            if (formDistrict) fd.append('district', formDistrict);
            if (formCity) fd.append('city', formCity);
            if (formPostalCode.trim()) fd.append('postal_code', formPostalCode.trim());
            if (formCountry.trim()) fd.append('country', formCountry.trim().slice(0, 2).toUpperCase());
            if (formProfilePhoto) fd.append('profile_photo', formProfilePhoto);
            if (formDepartmentId) fd.append('department_id', formDepartmentId);
            if (formJoiningDate) fd.append('joining_date', formJoiningDate);
            fd.append('is_teacher', formIsTeacher ? '1' : '0');
            if (formIsTeacher && formSubjectId) fd.append('primary_subject_id', formSubjectId);
            fd.append('is_active', formIsActive ? '1' : '0');
            if (schoolId) fd.append('school_id', schoolId);
            fd.append('role_id', String(formRoleId));
            if (drawerMode === 'edit') fd.append('_method', 'PUT');

            if (drawerMode === 'edit' && editingId) {
                await updateStaff(editingId, fd);
                setSuccessMsg(t('staffMgmt.updatedSuccess'));
            } else {
                await createStaff(fd);
                setSuccessMsg(t('staffMgmt.createdSuccess'));
            }

            closeDrawer();
            refresh();
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message ?? t('staffMgmt.failedToSave');
            setError(msg);
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setSuccessMsg(''), 5000);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteStaff(deleteTarget.id);
            setDeleteTarget(null);
            setSuccessMsg(t('staffMgmt.removedSuccess'));
            setTimeout(() => setSuccessMsg(''), 5000);
            refresh();
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message ?? t('staffMgmt.failedToRemove');
            setError(msg);
            setDeleteTarget(null);
        }
    };

    return (
        <div className="staff">
            {successMsg && (
                <div className="staff__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="staff__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {error && (
                <div className="staff__toast staff__toast--error" style={{ background: 'var(--color-error, #ef4444)' }}>
                    <span>{error}</span>
                    <button className="staff__toast-dismiss" onClick={() => setError('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="staff__header">
                <div className="staff__header-text">
                    <h1 className="staff__title">{t('staffMgmt.title')}</h1>
                    <p className="staff__subtitle">{t('staffMgmt.subtitle')}</p>
                </div>
                <div className="staff__header-actions">
                    <div className="staff__search-wrap">
                        <svg className="staff__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input className="staff__search" type="text" placeholder={t('staffMgmt.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button className="staff__add-btn" onClick={openCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        {t('staffMgmt.addStaff')}
                    </button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="staff__filters">
                <span className="staff__filter-label">{t('staffMgmt.department')}</span>
                <select className="staff__filter-select" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
                    <option value="">{t('staffMgmt.allDepartments')}</option>
                    {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
                <div className="staff__filter-divider" />
                <div className="staff__filter-toggle">
                    <span className="staff__filter-toggle-label">{t('staffMgmt.teachingOnly')}</span>
                    <label className="staff__mini-toggle">
                        <input type="checkbox" checked={teachingOnly} onChange={(e) => setTeachingOnly(e.target.checked)} />
                        <span className="staff__mini-toggle-track" />
                    </label>
                </div>
                <span className="staff__filter-count">{t('staffMgmt.staffCount', { count: total })}</span>
            </div>

            {/* Grid */}
            <div className="staff__table-wrap">
                {loading ? (
                    <div className="staff__loading"><div className="staff__loader"><span className="staff__loader-dot" /><span className="staff__loader-dot" /><span className="staff__loader-dot" /></div></div>
                ) : filtered.length === 0 ? (
                    <div className="staff__empty">
                        <div className="staff__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg></div>
                        <h3 className="staff__empty-title">{search || filterDept || teachingOnly ? t('staffMgmt.noStaffMatch') : t('staffMgmt.noStaffYet')}</h3>
                        <p className="staff__empty-text">{search || filterDept || teachingOnly ? t('staffMgmt.tryAdjustingFilters') : t('staffMgmt.addFirst')}</p>
                    </div>
                ) : (
                    <table className="staff__table">
                        <thead>
                            <tr>
                                <th className="staff__th">{t('common.name')}</th>
                                <th className="staff__th">{t('staffMgmt.employeeId')}</th>
                                <th className="staff__th">{t('staffMgmt.designation')}</th>
                                <th className="staff__th">{t('staffMgmt.department')}</th>
                                <th className="staff__th staff__th--actions">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => {
                                const name = getFullName(s);
                                return (
                                    <tr className="staff__table-row" key={s.id}>
                                        <td className="staff__td">
                                            <div className="staff__name-cell">
                                                <span className="staff__avatar">{getInitials(name)}</span>
                                                <div className="staff__name-text">
                                                    <span className="staff__name-primary">{name}</span>
                                                    <span className="staff__name-email">{s.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="staff__td staff__td--empid">{s.employee_id}</td>
                                        <td className="staff__td">{s.designation}</td>
                                        <td className="staff__td">{s.department}</td>
                                        <td className="staff__td staff__td--actions">
                                            <button className="staff__row-btn" onClick={() => openEdit(s)}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                {t('common.edit')}
                                            </button>
                                            <button className="staff__row-btn staff__row-btn--delete" style={{ marginLeft: 4 }} onClick={() => setDeleteTarget(s)}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="staff__pagination">
                    <button className="staff__page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                        {t('staffMgmt.prev')}
                    </button>
                    <span className="staff__page-info">{t('staffMgmt.pageOf', { page, total: totalPages })}</span>
                    <button className="staff__page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                        {t('staffMgmt.next')}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </div>
            )}

            {/* Drawer */}
            {drawerMode && (
                <>
                    <div className="staff__overlay" onClick={closeDrawer} />
                    <aside className="staff__drawer">
                        <div className="staff__drawer-header">
                            <h2 className="staff__drawer-title">{drawerMode === 'edit' ? t('staffMgmt.editStaff') : t('staffMgmt.addStaff')}</h2>
                            <button className="staff__drawer-close" onClick={closeDrawer}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="staff__drawer-body" onSubmit={handleSubmit} noValidate id="staff-form">
                            {/* Card 1: User Profile */}
                            <div className="staff__card">
                                <h3 className="staff__card-title">{t('staffMgmt.userProfile')}</h3>
                                <div className="staff__photo-area">
                                    <button
                                        type="button"
                                        className="staff__photo-picker"
                                        onClick={() => photoInputRef.current?.click()}
                                        title="Upload profile photo"
                                    >
                                        {formPhotoPreview ? (
                                            <img src={formPhotoPreview} alt="Preview" className="staff__photo-preview" />
                                        ) : (
                                            <>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                    <polyline points="21 15 16 10 5 21" />
                                                </svg>
                                                <span className="staff__photo-text">{t('addStudent.addPhoto')}</span>
                                            </>
                                        )}
                                    </button>
                                    <input
                                        ref={photoInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="staff__file-hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] ?? null;
                                            setFormProfilePhoto(file);
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onload = (ev) => setFormPhotoPreview(ev.target?.result as string);
                                                reader.readAsDataURL(file);
                                            } else {
                                                setFormPhotoPreview(null);
                                            }
                                        }}
                                    />
                                    {formProfilePhoto && (
                                        <div className="staff__photo-info">
                                            <span className="staff__photo-name">{formProfilePhoto.name}</span>
                                            <button type="button" className="staff__photo-remove" onClick={() => { setFormProfilePhoto(null); setFormPhotoPreview(null); if (photoInputRef.current) photoInputRef.current.value = ''; }} title="Remove photo">×</button>
                                        </div>
                                    )}
                                    {!formProfilePhoto && <span className="staff__photo-hint">{t('addStudent.photoHint')}</span>}
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label staff__label--required">{t('addStudent.firstName')}</label>
                                        <input className={`staff__input ${errors.firstName ? 'staff__input--error' : ''}`} type="text" placeholder="e.g. Anjali" value={formFirstName} onChange={(e) => { setFormFirstName(e.target.value); if (errors.firstName) setErrors((p) => ({ ...p, firstName: undefined })); }} autoFocus />
                                        {errors.firstName && <p className="staff__error">{errors.firstName}</p>}
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">{t('addStudent.lastName')}</label>
                                        <input className="staff__input" type="text" placeholder="e.g. Sharma" value={formLastName} onChange={(e) => setFormLastName(e.target.value)} />
                                    </div>
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('common.email')}</label>
                                        <input className="staff__input" type="email" placeholder="email@school.edu" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">{t('common.phone')}</label>
                                        <input className="staff__input" type="tel" placeholder="+91 9876543210" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
                                    </div>
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('addStudent.dateOfBirth')}</label>
                                        <input className="staff__input" type="date" value={formDob} onChange={(e) => setFormDob(e.target.value)} />
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">{t('addStudent.gender')}</label>
                                        <select className="staff__select" value={formGender} onChange={(e) => setFormGender(e.target.value)}>
                                            <option value="">{t('common.selectGender')}</option>
                                            <option value="M">{t('common.male')}</option>
                                            <option value="F">{t('common.female')}</option>
                                            <option value="Other">{t('common.other')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Card: Address */}
                            <div className="staff__card">
                                <h3 className="staff__card-title">{t('addStudent.address')}</h3>
                                <div className="staff__field">
                                    <label className="staff__label">{t('addStudent.streetAddress')}</label>
                                    <input className="staff__input" type="text" placeholder="e.g. 12 MG Road" value={formAddress} onChange={(e) => setFormAddress(e.target.value)} />
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('common.state')}</label>
                                        <select
                                            className="staff__select"
                                            value={formState}
                                            onChange={(e) => {
                                                const selected = states.find((s) => s.name === e.target.value);
                                                setFormState(e.target.value);
                                                setFormStateId(selected?.id ?? null);
                                                setFormDistrict('');
                                                setFormCity('');
                                                setDistricts([]);
                                                setCities([]);
                                                if (selected) {
                                                    setAddressLoading(true);
                                                    Promise.all([
                                                        fetchDistrictsByStateId(selected.id),
                                                        fetchCitiesByStateId(selected.id),
                                                    ])
                                                        .then(([d, c]) => { setDistricts(d); setCities(c); })
                                                        .catch(() => {})
                                                        .finally(() => setAddressLoading(false));
                                                }
                                            }}
                                        >
                                            <option value="">{t('staffMgmt.selectState')}</option>
                                            {states.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">District</label>
                                        <select className="staff__select" value={formDistrict} onChange={(e) => setFormDistrict(e.target.value)} disabled={!formState || addressLoading}>
                                            <option value="">{addressLoading ? t('common.loading') : !formState ? t('staffMgmt.selectStateFirst') : 'Select District…'}</option>
                                            {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('common.city')}</label>
                                        <select className="staff__select" value={formCity} onChange={(e) => setFormCity(e.target.value)} disabled={!formState || addressLoading}>
                                            <option value="">{addressLoading ? t('common.loading') : !formState ? t('staffMgmt.selectStateFirst') : t('staffMgmt.selectCity')}</option>
                                            {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">{t('common.postalCode')}</label>
                                        <input className="staff__input" type="text" placeholder="e.g. 560001" maxLength={20} value={formPostalCode} onChange={(e) => setFormPostalCode(e.target.value)} />
                                    </div>
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('staffMgmt.countryCode')}</label>
                                        <input className="staff__input" type="text" placeholder="e.g. IN" maxLength={2} value={formCountry} onChange={(e) => setFormCountry(e.target.value.toUpperCase())} />
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Employment Info */}
                            <div className="staff__card">
                                <h3 className="staff__card-title">{t('staffMgmt.employmentInfo')}</h3>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label staff__label--required">{t('staffMgmt.designation')}</label>
                                        <select
                                            className={`staff__select ${errors.roleId ? 'staff__input--error' : ''}`}
                                            value={formRoleId ?? ''}
                                            onChange={(e) => {
                                                setFormRoleId(e.target.value ? Number(e.target.value) : null);
                                                if (errors.roleId) setErrors((p) => ({ ...p, roleId: undefined }));
                                            }}
                                        >
                                            <option value="">{t('staffMgmt.selectDesignation')}</option>
                                            {roles.map((r) => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                        {errors.roleId && <p className="staff__error">{errors.roleId}</p>}
                                    </div>
                                </div>
                                <div className="staff__form-row">
                                    <div className="staff__field">
                                        <label className="staff__label">{t('staffMgmt.department')}</label>
                                        <select className={`staff__select ${errors.department ? 'staff__input--error' : ''}`} value={formDepartmentId} onChange={(e) => {
                                            setFormDepartmentId(e.target.value);
                                            if (errors.department) setErrors((p) => ({ ...p, department: undefined }));
                                        }}>
                                            <option value="">Select…</option>
                                            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                        {errors.department && <p className="staff__error">{errors.department}</p>}
                                    </div>
                                    <div className="staff__field">
                                        <label className="staff__label">{t('staffMgmt.joiningDate')}</label>
                                        <input className="staff__input" type="date" value={formJoiningDate} onChange={(e) => setFormJoiningDate(e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {/* Card 3: Academic Role */}
                            <div className="staff__card">
                                <h3 className="staff__card-title">{t('staffMgmt.academicRole')}</h3>
                                <div className="staff__toggle-row">
                                    <div className="staff__toggle-info">
                                        <span className="staff__toggle-label">{t('staffMgmt.teachingFaculty')}</span>
                                        <span className="staff__toggle-desc">{t('staffMgmt.teachingFacultyDesc')}</span>
                                    </div>
                                    <label className="staff__toggle">
                                        <input type="checkbox" checked={formIsTeacher} onChange={(e) => setFormIsTeacher(e.target.checked)} />
                                        <span className="staff__toggle-track" />
                                    </label>
                                </div>
                                {formIsTeacher && (
                                    <div className="staff__conditional">
                                        <div className="staff__field">
                                            <label className="staff__label">{t('staffMgmt.primarySubject')}</label>
                                            <select className="staff__select" value={formSubjectId} onChange={(e) => setFormSubjectId(e.target.value)}>
                                                <option value="">{t('staffMgmt.selectSubject')}</option>
                                                {subjects.map((s) => {
                                                    const subj = s.organization_subject?.subject;
                                                    const name = subj?.name ?? s.id;
                                                    const code = subj?.code ?? '';
                                                    return <option key={s.organization_subject?.id} value={s.organization_subject?.id}>{name}{code ? ` (${code})` : ''}</option>;
                                                })}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                <div className="staff__toggle-row" style={{ marginTop: 12 }}>
                                    <div className="staff__toggle-info">
                                        <span className="staff__toggle-label">{t('common.active')}</span>
                                        <span className="staff__toggle-desc">{t('staffMgmt.activeDesc')}</span>
                                    </div>
                                    <label className="staff__toggle">
                                        <input type="checkbox" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />
                                        <span className="staff__toggle-track" />
                                    </label>
                                </div>
                            </div>

                            {drawerMode === 'edit' && editCreatedAt && (
                                <div className="staff__timestamps">
                                    <span className="staff__timestamp">{t('staffMgmt.createdAt', { date: formatDate(editCreatedAt) })}</span>
                                    <span className="staff__timestamp">{t('staffMgmt.updatedAt', { date: formatDate(editUpdatedAt) })}</span>
                                </div>
                            )}
                        </form>
                        <div className="staff__drawer-footer">
                            <button className="staff__submit" type="submit" form="staff-form" disabled={isSubmitting}>
                                {isSubmitting ? (<><svg className="staff__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>{drawerMode === 'edit' ? t('staffMgmt.updating') : t('staffMgmt.adding')}</>) : drawerMode === 'edit' ? t('staffMgmt.updateStaff') : t('staffMgmt.addStaff')}
                            </button>
                            <button className="staff__cancel" type="button" onClick={closeDrawer} disabled={isSubmitting}>{t('common.cancel')}</button>
                        </div>
                    </aside>
                </>
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <div className="staff__confirm-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="staff__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="staff__confirm-title">{t('staffMgmt.removeStaff')}</h3>
                        <p className="staff__confirm-text">
                            <Trans i18nKey="staffMgmt.confirmRemove" values={{ name: getFullName(deleteTarget) }} components={[<span />, <strong />]} />
                        </p>
                        <div className="staff__confirm-actions">
                            <button className="staff__confirm-cancel" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</button>
                            <button className="staff__confirm-delete" onClick={confirmDelete}>{t('staffMgmt.remove')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
